'use strict';
import isObject from 'lodash/isObject';
import {getCollection, getDomainInstance, getDomainLink, getId, getLink} from '../../core-utilities/domain/ReduxUtils';
import {ASSET_PROJECT_COLLECTION} from '../../projects/domain/AssetProjectDomain';
import {getOrganization} from '../../app/model/reducers/state';


export const CLASS_NAME = 'ConversationDomain';
export const COLLECTION = 'conversations';
export const CHAT_CHANNEL_COLLECTION = 'conversations';

export function ConversationDomainProvider() {
  return {
    $get: [
      '$injector',
      'FirebaseUtil',
      'AuthService',
      '$ngRedux',
      '$domain',
      '$firebaseObject',
      'AbstractDomainClass',
      'FirebaseQueueService',
      'fetchUtils',
      'ROOT_API_COLLECTION',
      'ROOT_API_SPEC',
      function (
        $injector,
        FirebaseUtil,
        AuthService,
        $ngRedux,
        $domain,
        $firebaseObject,
        AbstractDomainClass,
        FirebaseQueueService,
        fetchUtils,
        ROOT_API_COLLECTION,
        ROOT_API_SPEC,
      ) {
        //console.log('Calling ConversationDomain');
        function ConversationDomain(data) {
          AbstractDomainClass.call(this, ConversationDomain, data);
        }

        AbstractDomainClass.__inheritFrom(ConversationDomain, CLASS_NAME, COLLECTION);

        ConversationDomain.$constraints.addConstraints({
          org: {type: 'string'},
          name: {type: 'string'},
          target: {type: 'Domain', nullable: true}, /* the optional channel topic*/
          latestComment: {type: 'Domain', domainClass: 'CommentDomain', nullable: true},
          metadata: {type: 'JSON', nullable: true},
          commentCount: {type: 'number', min: 0},
          createdBy: {type: 'DomainLink', domainClass: 'UserDomain', nullable: true},
          dateCreated: {type: 'Date'},
          lastUpdated: {type: 'Date'},

          /* Links and Collections */
          comments: {type: 'DomainCollection', domainClass: 'CommentDomain', link: 'conversation'},
          authors: {type: 'DomainCollection', domainClass: 'PersonDomain'},
          subscribers: {type: 'DomainCollection', domainClass: 'UserDomain', links: 'conversations-subscriptions'}
        });

        // more methods go here;

        ConversationDomain.getLinked = function getLinked(channelId, collection) {
          return ConversationDomain.$get(channelId).then(function withExistingConvo(existingConvo) {
            // Temporary Hack. User will not have access if the object isn't fully initialized once next round of rules are in place
            // Specifically, afterwards, remove "&& existingConvo.dateCreated"
            if (existingConvo && existingConvo.dateCreated) {
              return existingConvo;
            } else {
              return ConversationDomain.createLinked(channelId, collection)
                .then(ConversationDomain.$get.bind(ConversationDomain, channelId));
            }
          }, function onError() { // try to create the linked convo in case it was not initialized correctly?
            return ConversationDomain.createLinked(channelId, collection)
              .then(ConversationDomain.$get.bind(ConversationDomain, channelId));
          })
        };

        ConversationDomain.createLinked = function createLinked(channelId, collection) {
          const orgId = getId(getOrganization($ngRedux.getState()));
          if (!orgId) return Promise.reject('No organization selected');
          const request = {
            method: 'chat/linked/create',
            organization: orgId,
            id: channelId,
            collection: collection
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        };

        ConversationDomain.getCommentCountRef = function getCommentCountRef(commentableId) {
          return FirebaseUtil.firebaseRef([
            ConversationDomain.COLLECTION,
            commentableId,
            'commentCount'
          ].join('/'))
        };

        ConversationDomain.bindCommentCount = function bindCommentCount(scope, name, commentableId) {
          if (!commentableId) throw new Error('required a valid commentable ID');
          var fbo = $firebaseObject(ConversationDomain.getCommentCountRef(commentableId));
          scope.$on('$destroy', fbo.$destroy.bind(fbo));
          return fbo;
        };

        ConversationDomain.getCommentCount = function getCommentCount(commentableId) {
          if (commentableId) {
            return FirebaseUtil.read([
              ConversationDomain.COLLECTION,
              commentableId,
              'commentCount'
            ].join('/'));
          } else {
            return Promise.reject('required a valid commentable ID');
          }
        };

        ConversationDomain.checkName = function checkName(channelId) {
          return ConversationDomain.$get(channelId).then(conversation => {
            if (!conversation || !getLink(conversation, 'target').id) {
              return true; // consider this a match
            } else {
              return Promise.props({
                conversation: Promise.resolve(conversation),
                targetName: ConversationDomain.getTargetName(getLink(conversation, 'target')),
              });
            }
          }).then(data => {
            const targetName = data.targetName;
            if (data === true)
              return;
            if (equalsString(data.conversation.name, targetName))
              return;
            // They are no longer equal, update the name
            try {
              const request = {
                method: 'PATCH',
                mode: 'cors',
                cache: 'default',
                contentType: 'json',
                responseType: 'json',
                body: {
                  name: targetName,
                },
              };
              const url = [
                fetchUtils.API_URL,
                '/api/chat-channels/',
                channelId,
              ].join('');
              return fetchUtils.requestWithAuth(url, request);
            } catch (e) {
              console.error(e);
            }
          });
        };

        ConversationDomain.getTargetName = link => {
          if (getCollection(link) === ASSET_PROJECT_COLLECTION) {
            return $domain.getDomainInstance(link).then(target => target ? target.getFullName() : null);
          } else {
            return $domain.getDomainInstance(link).then(target => target ? target.toString() : null);
          }
        };

        function equalsString(a, b) {
          if (a && !b || !a && b)
            return false;
          else if (!a && !b) // we don't care if one is "" and one is null here.
            return true;
          else
            return a === b;
        }

        ConversationDomain.post = function postStatic(conversation, comment) {
          const request = {
            method: 'chat/post',
            conversation: conversation,
            message: comment.message,
            callouts: comment.callouts || null,
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        };

        /**
         *
         * @param {CommentDomain} comment
         * @returns {Promise}
         */
        ConversationDomain.prototype.post = function post(comment) {
          return ConversationDomain.post(this.$id, comment);
        };

        ConversationDomain.getTarget = function getTarget(model) {
          if (isObject(model) && getLink(model, 'target').id) {
              return getDomainInstance(getLink(model, 'target'));
          } else {
            return null;
          }
        };

        ConversationDomain.prototype.getTarget = function getTarget() {
          if (getLink(this, 'target').id) {
            return getDomainLink(this, 'target');
          } else {
            return Promise.resolve(null);
          }
        };

        ConversationDomain.prototype.toString = function toString() {
          return this.name;
        };

        return ConversationDomain;
      }]
  };
}



// WEBPACK FOOTER //
// ./src/js/comments/domain/ConversationDomain.js